/**
 * RouteHealthReporter — fire-and-forget client helper to log broken pages
 * to /api/platform-public/route-error.
 *
 * Usage (manual): RouteHealthReporter.report({ kind: "not_found" })
 * The RouteErrorBoundary + NotFoundCatcher components call this automatically.
 *
 * Safe: any failure is swallowed; never blocks rendering.
 * Deduped: identical signatures are only sent once per tab-session (the
 * backend also dedups, but this cuts network noise).
 */
const SESSION_SENT_KEY = "__route_health_sent";

function alreadySent(sig) {
  try {
    const raw = sessionStorage.getItem(SESSION_SENT_KEY);
    const set = raw ? new Set(JSON.parse(raw)) : new Set();
    if (set.has(sig)) return true;
    set.add(sig);
    sessionStorage.setItem(SESSION_SENT_KEY, JSON.stringify([...set].slice(-50)));
    return false;
  } catch { return false; }
}

function getTenantId() {
  try { return localStorage.getItem("public_tenant_id") || null; } catch { return null; }
}

async function report({ kind, message, stack }) {
  try {
    const url_path = window.location.pathname + window.location.search;
    const signature = `${kind}::${url_path}::${(message || "").slice(0, 80)}`;
    if (alreadySent(signature)) return;

    const base = process.env.REACT_APP_BACKEND_URL || "";
    // Use sendBeacon when possible so the report survives page navigation.
    const payload = JSON.stringify({
      url_path,
      error_kind: kind,
      error_message: (message || null),
      stack_preview: stack ? String(stack).slice(0, 3800) : null,
      tenant_id: getTenantId(),
      referrer: document.referrer || null,
    });
    const target = `${base}/api/platform-public/route-error`;
    if (navigator.sendBeacon) {
      const ok = navigator.sendBeacon(target, new Blob([payload], { type: "application/json" }));
      if (ok) return;
    }
    fetch(target, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      keepalive: true,
    }).catch(() => {});
  } catch { /* noop */ }
}

const RouteHealthReporter = { report };
export default RouteHealthReporter;
